import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getUsers, updateUser, deleteUser, createUserByAdmin, uploadUsersCSV } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Users, 
  Plus, 
  Search, 
  Check, 
  X, 
  Trash2,
  MoreVertical,
  Upload,
  FileSpreadsheet,
  Download
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function ClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    password: '',
  });

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const response = await getUsers(status);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await updateUser(userId, { status: 'approved' });
      toast.success('Client approved successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve client');
    }
  };

  const handleReject = async (userId) => {
    try {
      await updateUser(userId, { status: 'rejected' });
      toast.success('Client rejected');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reject client');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await deleteUser(userId);
      toast.success('Client deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUserByAdmin(newUser);
      toast.success('Client created successfully');
      setCreateDialogOpen(false);
      setNewUser({ name: '', email: '', company: '', phone: '', password: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);
    
    try {
      const response = await uploadUsersCSV(file);
      setUploadResult(response.data);
      toast.success(`Created ${response.data.created} clients`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = 'name,email,password,company,phone\nJohn Doe,john@example.com,Welcome123!,Acme Inc,+1 555-0001\nJane Smith,jane@example.com,Welcome123!,Tech Corp,+1 555-0002';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    if (value === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', value);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Clients</h1>
          <p className="text-slate-400">Manage your client accounts and approvals</p>
        </div>
        
        <div className="flex gap-2">
          {/* CSV Upload Dialog */}
          <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-white/10" data-testid="csv-upload-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111827] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Import Clients from CSV</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Upload a CSV file to bulk create client accounts. Clients will be created with "approved" status.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-sm text-slate-300 mb-2">Required CSV columns:</p>
                  <code className="text-xs text-[#4A90D9] bg-black/30 px-2 py-1 rounded">
                    name, email, password (optional), company (optional), phone (optional)
                  </code>
                </div>
                
                <Button 
                  variant="outline" 
                  onClick={downloadTemplate}
                  className="w-full border-slate-700 text-slate-300 hover:bg-white/10"
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>

                <div className="relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full bg-[#4A90D9] hover:bg-[#3A80C9]"
                    data-testid="upload-csv-file-btn"
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Select CSV File
                      </>
                    )}
                  </Button>
                </div>

                {uploadResult && (
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-2">
                    <p className="text-sm text-white">
                      <span className="text-green-400 font-medium">{uploadResult.created}</span> clients created
                    </p>
                    <p className="text-sm text-white">
                      <span className="text-yellow-400 font-medium">{uploadResult.skipped}</span> skipped
                    </p>
                    {uploadResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-red-400 mb-1">Errors:</p>
                        <ul className="text-xs text-slate-400 space-y-1">
                          {uploadResult.errors.map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Create Single Client Dialog */}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="create-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Client</DialogTitle>
              <DialogDescription className="text-slate-400">
                Create a new client account with immediate approval.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label className="text-slate-300">Full Name *</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="new-client-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="new-client-email"
                />
              </div>
              <div>
                <Label className="text-slate-300">Company</Label>
                <Input
                  value={newUser.company}
                  onChange={(e) => setNewUser({ ...newUser, company: e.target.value })}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="new-client-company"
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <Input
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="new-client-phone"
                />
              </div>
              <div>
                <Label className="text-slate-300">Password *</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  minLength={6}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="new-client-password"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setCreateDialogOpen(false)}
                  className="text-slate-400"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="submit-create-client">
                  Create Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#111827] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1E293B] border-slate-700 text-white"
                data-testid="search-clients"
              />
            </div>
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-full sm:w-48 bg-[#1E293B] border-slate-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Clients List */}
      <Card className="bg-[#111827] border-white/10">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No clients found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} data-testid={`client-row-${user.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#4A90D9]/20 flex items-center justify-center text-[#4A90D9] font-medium">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-slate-300">{user.company || '-'}</td>
                      <td>
                        <Badge className={`badge-${user.status}`}>
                          {user.status}
                        </Badge>
                      </td>
                      <td className="text-slate-400 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {user.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-green-500 hover:text-green-400 hover:bg-green-500/10"
                                onClick={() => handleApprove(user.id)}
                                data-testid={`approve-${user.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                onClick={() => handleReject(user.id)}
                                data-testid={`reject-${user.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-slate-400">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#1E293B] border-slate-700">
                              <DropdownMenuItem 
                                className="text-red-500 focus:text-red-400 focus:bg-red-500/10"
                                onClick={() => handleDelete(user.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
